/**
 * Re-export `createConfig` from @zeiss/beyond-online-react.
 *
 * This is necessary to resolve the issue where the contexts didn't carry
 * over well to other @zeiss/lib-components elements that require them.
 * For instance the `linkComponent (ReactRouterLinkComponent)` didn't work
 * well in our custom snowflakes/component overrides.
 *
 * The root cause being that when we build the `@zeiss/lib-components` library
 * the references (functions, variables) are minified leading to wrong references
 * in the final usage.
 *
 * Re-exporting this `createConfig` resolves this issue.
 */
import {
  // eslint-disable-next-line no-restricted-imports
  createConfig as beyondCreateConfig,
  type GlobalConfig,
} from '@zeiss/beyond-online-react';

type BeyondCreateConfigInput = Parameters<typeof beyondCreateConfig>[0];

type CreateConfigInput = Omit<BeyondCreateConfigInput, 'global'> & {
  global?: Omit<
    NonNullable<BeyondCreateConfigInput['global']>,
    'linkComponent'
  > & {
    linkComponent?: GlobalConfig['global']['linkComponent'];
  };
};

/**
 * `@zeiss/beyond-online-react` publishes `createConfig` as `DeepPartial<GlobalConfig>`.
 * Newer `type-fest` versions deep-partialize component types and break `linkComponent`.
 * Narrow the input here so consumers can keep passing a real component.
 */
export const createConfig = (config: CreateConfigInput): GlobalConfig => {
  return beyondCreateConfig(config as BeyondCreateConfigInput);
};

export { GlobalConfigProvider, useGlobalConfig } from './GlobalConfig';
